import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { PageHeader, Banner } from '../components/ui';
import { jobPath } from '../nav/paths';

const th = { textAlign: 'left', padding: '6px 8px', fontSize: 12, color: 'var(--gray-500)', borderBottom: '1px solid var(--gray-200)', whiteSpace: 'nowrap' };
const td = { padding: '6px 8px', fontSize: 13, borderBottom: '1px solid var(--gray-100)', verticalAlign: 'top' };

function JobTable({ jobs }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
      <thead>
        <tr>
          <th style={th}>Job</th>
          <th style={th}>Customer</th>
          <th style={th}>Project Manager</th>
          <th style={th}>Director</th>
        </tr>
      </thead>
      <tbody>
        {jobs.map((j) => (
          <tr key={j.jobNo}>
            <td style={td}>
              <Link to={jobPath(j.jobNo)} style={{ color: 'var(--primary)', fontWeight: 600, textDecoration: 'none' }}>{j.jobNo}</Link>
              {j.jobDescription ? ` — ${j.jobDescription}` : ''}
            </td>
            <td style={td}>{j.isOverhead ? '' : j.customerName}</td>
            <td style={td}>{j.projectManagerName || j.projectManager}</td>
            <td style={td}>{j.directorName || j.director}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Jobs > Job List: the jobs the user can open (Project Manager or Director;
// every active job for Accounting / Executive), each opening its job page.
// Overhead jobs are a separate section, hidden until the user shows them.
export default function JobListPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [showOverhead, setShowOverhead] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.getJobList();
      setJobs(Array.isArray(r.jobs) ? r.jobs : []);
    } catch (e) {
      setError((e && e.message) || 'Failed to load the job list');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const q = search.trim().toLowerCase();
  const match = useCallback((j) => !q || [j.jobNo, j.jobDescription, j.customerName, j.projectManagerName, j.directorName]
    .some((v) => String(v || '').toLowerCase().includes(q)), [q]);
  const regular = useMemo(() => jobs.filter((j) => !j.isOverhead && match(j)), [jobs, match]);
  const overhead = useMemo(() => jobs.filter((j) => j.isOverhead && match(j)), [jobs, match]);

  return (
    <div style={{ padding: '8px 16px 32px' }}>
      <PageHeader
        title="Job List"
        onRefresh={load}
        refreshing={loading}
        actions={(
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, color: 'var(--gray-600)', marginRight: 4 }}>
            <input type="checkbox" checked={showOverhead} onChange={(e) => setShowOverhead(e.target.checked)} />
            Show overhead jobs
          </label>
        )}
      />
      <input className="form-control" type="search" placeholder="Search jobs" value={search}
        onChange={(e) => setSearch(e.target.value)} style={{ marginBottom: 10 }} />
      <Banner kind="err">{error}</Banner>
      {loading ? <div style={{ padding: 16 }}>Loading jobs…</div> : (
        <>
          {regular.length ? <JobTable jobs={regular} /> : <div style={{ padding: 16 }}>No jobs found.</div>}
          {showOverhead && (
            <>
              <h3 style={{ margin: '20px 0 8px' }}>Overhead Jobs</h3>
              {overhead.length ? <JobTable jobs={overhead} /> : <div style={{ padding: 16 }}>No overhead jobs found.</div>}
            </>
          )}
        </>
      )}
    </div>
  );
}
